import * as React from 'react';
import { isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import { FieldBase, type FieldBaseProps } from 'view/common/fields/field-base';
import type { SelectProps } from '@atlaskit/select/types';
import type { Option } from '@atlaskit/select';

export interface SelectFieldFormProps {
	id: string;
	errorMessage?: string;
	label: string;
	required?: boolean;
	description?: string;
	isClearable?: boolean;
	value?: Option<string> | null;
}

function withFieldBase<V extends Option<string>, T extends SelectProps<V, K>, K extends boolean>(
	SelectComponent: React.ComponentType<T>,
) {
	const wrappedComponent: React.FunctionComponent<
		SelectFieldFormProps & T & Partial<FieldBaseProps>
	> = (props: SelectFieldFormProps & SelectProps<V, K> & Partial<FieldBaseProps>) => {
		const { label, required, errorMessage, description, id, tooltip, ...otherProps } = props;

		return (
			<FieldBase
				htmlFor={id}
				label={label}
				required={required}
				errorMessage={errorMessage}
				description={description}
				{...(isUiModificationsSupportEnabled() ? { tooltip } : null)}
			>
				<SelectComponent
					// Bug introduced with spread operator. Since we have a complex case here
					// we can't just cast it properly.
					// https://github.com/Microsoft/TypeScript/issues/28938#issuecomment-450636046
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					{...(otherProps as any)}
					inputId={id}
					aria-describedby={`${id}-field-description`}
					validationState={!!errorMessage ? 'error' : 'default'}
				/>
			</FieldBase>
		);
	};

	return wrappedComponent;
}

export default withFieldBase;
